// src/products/capRaise/pages/DashboardPage.js
// Redesigned dashboard with gallery-style round cards

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  ArrowRight,
  CalendarDays
} from 'lucide-react';
import { supabase } from '../../../config';
import capRaiseApi from '../services/capRaiseApi';

const DashboardPage = ({
  selectedCompany,
  activeRound,
  user,
  addMessage,
  onCompanyChange,
  onRoundChange
}) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadCompanies = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('equity_companies')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      addMessage?.('Error loading companies', 'error');
    }
  }, [user?.id, addMessage]);

  const loadRounds = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await capRaiseApi.getRounds();
      if (error) throw error;
      setRounds(data || []);
    } catch (error) {
      console.error('Error loading rounds:', error);
      addMessage?.('Error loading rounds', 'error');
    } finally {
      setLoading(false);
    }
  }, [addMessage]);

  useEffect(() => {
    loadCompanies();
    loadRounds();
  }, [loadCompanies, loadRounds]);

  const companyLookup = useMemo(() => {
    const map = {};
    companies.forEach(company => {
      map[company.id] = company;
    });
    return map;
  }, [companies]);

  const filteredRounds = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rounds.filter(round => {
      const matchesStatus = statusFilter === 'all' || round.status === statusFilter;
      const matchesCompany = companyFilter === 'all' || String(round.company_id) === String(companyFilter);
      const companyName = companyLookup[round.company_id]?.company_name || companyLookup[round.company_id]?.name || '';
      const matchesSearch =
        !term ||
        round.round_name?.toLowerCase().includes(term) ||
        companyName.toLowerCase().includes(term);
      return matchesStatus && matchesCompany && matchesSearch;
    });
  }, [rounds, statusFilter, companyFilter, searchTerm, companyLookup]);

  const metrics = useMemo(() => {
    const totalTarget = rounds.reduce((sum, round) => sum + (round.target_amount || 0), 0);
    const activeRounds = rounds.filter(round => round.status === 'active').length;
    const draftRounds = rounds.filter(round => round.status === 'draft').length;
    return {
      totalRounds: rounds.length,
      activeRounds,
      draftRounds,
      totalTarget
    };
  }, [rounds]);

  const statusBadgeClass = (status = 'draft') => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-gray-200 text-gray-700';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleCardSelect = (round) => {
    const company = companyLookup[round.company_id];
    if (company && (!selectedCompany || company.id !== selectedCompany.id)) {
      onCompanyChange?.(company);
    }
    onRoundChange?.(round);
  };

  // const handleViewWorkspace = (round, e) => {
  //   e?.stopPropagation();
  //   handleCardSelect(round);
  //   navigate('/capraise/new-raise', { state: { roundId: round.id } });
  // }; // Unused

  const handleOpenTermsheet = (round, e) => {
    e?.stopPropagation();
    handleCardSelect(round);
    navigate('/capraise/termsheet', { state: { roundId: round.id } });
  };

  const handleOpenScenarios = (round, e) => {
    e?.stopPropagation();
    handleCardSelect(round);
    navigate('/capraise/scenarios', { state: { roundId: round.id } });
  };

  const handleCreateRound = () => {
    // Clear active round when creating a new one
    onRoundChange?.(null);
    navigate('/capraise/new-raise');
  };

  const formatCurrency = (value, currency = 'USD') => capRaiseApi.formatCurrency(value || 0, currency);

  const formatRoundType = (roundType) => {
    if (!roundType) return '—';
    // Convert snake_case to Title Case (e.g., series_a -> Series A)
    return roundType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch {
      return '—';
    }
  };

  const companyOptions = useMemo(() => [
    { id: 'all', label: 'All companies' },
    ...companies.map(company => ({
      id: company.id,
      label: company.company_name || company.name
    }))
  ], [companies]);

  const statusOptions = [
    { value: 'all', label: 'All statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'closed', label: 'Closed' }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <section className="bg-white rounded-2xl border shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Your Capital Raising Rounds</h1>
          </div>
          <button
            onClick={handleCreateRound}
            className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow"
          >
            <Plus className="w-4 h-4 mr-2" />
            Start new round
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div className="p-3 border rounded-2xl bg-gray-50">
            <p className="text-xs uppercase text-gray-500 font-semibold">Total rounds</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.totalRounds}</p>
          </div>
          <div className="p-3 border rounded-2xl bg-gray-50">
            <p className="text-xs uppercase text-gray-500 font-semibold">Active rounds</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.activeRounds}</p>
          </div>
          <div className="p-3 border rounded-2xl bg-gray-50">
            <p className="text-xs uppercase text-gray-500 font-semibold">Drafts</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.draftRounds}</p>
          </div>
          <div className="p-3 border rounded-2xl bg-gray-50">
            <p className="text-xs uppercase text-gray-500 font-semibold">Target capital</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {formatCurrency(metrics.totalTarget, activeRound?.currency || 'USD')}
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white rounded-2xl border p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search rounds or companies"
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-1 flex-wrap gap-3 justify-end">
          <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2">
            <Filter className="w-4 h-4 text-gray-400 mr-2" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent text-sm text-gray-700 focus:outline-none"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="border border-gray-200 rounded-xl px-3 py-2">
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="bg-transparent text-sm text-gray-700 focus:outline-none"
            >
              {companyOptions.map(option => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(item => (
            <div key={item} className="p-5 border rounded-2xl bg-white animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
              <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      ) : filteredRounds.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredRounds.map((round) => {
            const companyName = companyLookup[round.company_id]?.company_name || companyLookup[round.company_id]?.name || 'Unknown company';
            const isActive = activeRound?.id === round.id;
            return (
              <div
                key={round.id}
                className={`p-5 border rounded-2xl bg-white hover:shadow-lg transition cursor-pointer ${
                  isActive ? 'border-blue-500 shadow-blue-100' : 'border-gray-200'
                }`}
                onClick={() => handleCardSelect(round)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase text-gray-500 font-semibold">{companyName}</p>
                    <h3 className="text-xl font-semibold text-gray-900">{round.round_name}</h3>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusBadgeClass(round.status)}`}>
                    {round.status || 'draft'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4 text-sm text-gray-600">
                  <div>
                    <p className="text-xs uppercase text-gray-400">Target</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(round.target_amount, round.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-gray-400">Round type</p>
                    <p className="text-base font-semibold text-gray-900">{formatRoundType(round.round_type)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                  <span className="inline-flex items-center">
                    <CalendarDays className="w-3.5 h-3.5 mr-1" />
                    Updated {formatDate(round.updated_at)}
                  </span>
                  {isActive && <span className="text-blue-600 font-medium">Active round</span>}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={(e) => handleOpenTermsheet(round, e)}
                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    See Termsheet
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                  <button
                    onClick={(e) => handleOpenScenarios(round, e)}
                    className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    See Scenarios
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border rounded-2xl p-10 text-center">
          <p className="text-gray-600 mb-4">No rounds match your filters yet.</p>
          <button
            onClick={handleCreateRound}
            className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create your first round
          </button>
        </div>
      )}

      <button
        onClick={handleCreateRound}
        className="fixed bottom-8 right-8 inline-flex items-center px-4 py-3 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700"
      >
        <Plus className="w-4 h-4 mr-2" />
        New raise
      </button>
    </div>
  );
};

export default DashboardPage;
